import React from 'react'
import { useSelector } from 'react-redux'
import { LazyGrid } from 'components/common'
import { Badge } from 'primereact/badge'
import { FilterMatchMode } from 'primereact/api'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import ContactTypeFilterTemplate from 'components/common/CommonGrids/Filters/ContactTypeFilterTemplate'
import {
    selectContactSearchFirst,
    selectContactSearchRows,
    selectContactSearchTotalPages,
    selectContactSearchFetchStatus,
    selectContactSearchTerm
} from '../../store/selectors'
import {
    setContactSearchFirstAction,
    setContactSearchRowsAction,
    setContactSearchCurrentPageAction,
    setContactSearchTotalPagesAction,
    setContactSearchFilterAction,
    setContactSearchSortAction
} from '../../store/actions'

const SearchResultsPanel = ({
    searchResults,
    totalCount,
    selectedContact,
    onContactSelect
}) => {
    const first = useSelector(selectContactSearchFirst)
    const rows = useSelector(selectContactSearchRows)
    const searchTerm = useSelector(selectContactSearchTerm)
    const assets = useSelector(selectAllAssets)
    const companyTypes = assets?.companyTypes || []
    
    const contactTypeTemplate = (rowData) => {
        return (
            <span className={`p-tag custom-badge-base custom-badge-aqua-${rowData.contactTypeId || 1}`}>
                {rowData.contactType}
            </span>
        )
    }

    const companyTypeTemplate = (rowData) => {
        const companyTypeId = rowData.companyType
        if (!companyTypeId) return null
        
        // Parse to number to match the id type from the API
        const companyTypeIdNum = parseInt(companyTypeId, 10)
        
        // Find the company type by id
        const companyTypeObj = companyTypes.find(ct => ct.id === companyTypeIdNum)
        const typeName = companyTypeObj?.description || companyTypeId
        const typeId = companyTypeObj?.id || 0
        const shade = (parseInt(typeId, 10) || 0) % 10
        
        return (
            <Badge
                value={typeName}
                className={`custom-badge-base custom-badge-green-${shade}`}
            />
        )
    }

    const handleSelectionChange = (e) => {
        onContactSelect(e.value)
    }

    const handleKeyDown = (e) => {
        if (!searchResults?.length || !selectedContact) return

        const currentIndex = searchResults.findIndex(r => r.id === selectedContact.id)
        if (currentIndex === -1) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const nextIndex = Math.min(currentIndex + 1, searchResults.length - 1)
            if (nextIndex !== currentIndex) {
                onContactSelect(searchResults[nextIndex])
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prevIndex = Math.max(currentIndex - 1, 0)
            if (prevIndex !== currentIndex) {
                onContactSelect(searchResults[prevIndex])
            }
        }
    }

    const columnDefinitions = [
        { columnType: 'text', title: 'Code', mapsToField: 'code', filter: true, sortable: true },
        { columnType: 'text', title: 'Author Key', mapsToField: 'authorKey', filter: true, sortable: true },
        { columnType: 'text', title: 'Contact Name', mapsToField: 'correspondenceName', filter: true, sortable: true },
        { columnType: 'contacttype', title: 'Type', mapsToField: 'contactType', template: contactTypeTemplate, filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true },
        { columnType: 'companytype', title: 'Company Type', mapsToField: 'companyType', template: companyTypeTemplate, filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true }
    ]

    const header = totalCount > 0 ? (
        <div className="d-flex justify-content-end align-items-center">
            <Badge value={`Displaying ${first + 1}-${Math.min(first + rows, totalCount)}`} />
            <span className="mx-2" style={{ fontSize: '14px', fontWeight: '500', color: 'black' }}><b>of</b></span>
            <Badge value={totalCount} />
            <span className="ms-2" style={{ fontSize: '14px', fontWeight: '500', color: 'black' }}><b>contacts</b></span>
        </div>
    ) : null

    return (
        <div className="contact-search-panel">
            <LazyGrid
                id="contact-search-results"
                resetFiltersKey={searchTerm}
                dataKey="id"
                data={searchResults}
                totalCount={totalCount}
                columnDefinitions={columnDefinitions}
                emptyMessage="No results found"
                selectionMode="single"
                selection={selectedContact}
                onSelectionChange={handleSelectionChange}
                selectFirst={selectContactSearchFirst}
                selectRows={selectContactSearchRows}
                selectTotalPages={selectContactSearchTotalPages}
                fetchStatusSelector={selectContactSearchFetchStatus}
                setFirstAction={setContactSearchFirstAction}
                setRowsAction={setContactSearchRowsAction}
                setCurrentPageAction={setContactSearchCurrentPageAction}
                setTotalPagesAction={setContactSearchTotalPagesAction}
                setFilterAction={setContactSearchFilterAction}
                setSortAction={setContactSearchSortAction}
                header={header}
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}

export default SearchResultsPanel
